import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { AnalyticsService } from 'app/services/analytics.service';
import { Router } from '@angular/router';
import { InputComponent } from '@components/input/input.component';
import { ButtonComponent } from '@components/button/button.component';
import { AlertComponent } from '@components/alert/alert.component';
import { ApiService, UserInfo } from 'app/services/api.service';
import { switchMap, catchError, of } from 'rxjs';
import { environment } from 'environments/environment'

const SUPPLIER_MESSAGES: Record<string, { id: string; defaultMessage: string; type: 'error' | 'warning' }> = {
  PROVIDER_ALREADY_EXISTS: {
    id: 'suppliers.error.provider_already_exists',
    defaultMessage: 'La cuenta ya existe dada de alta',
    type: 'warning',
  },
  PROVIDER_VALIDATION_ERROR: {
    id: 'suppliers.error.provider_validation_error',
    defaultMessage: 'Algo ha ido mal en la solicitud',
    type: 'error',
  },
};

const DEFAULT_UPDATE_ERROR = SUPPLIER_MESSAGES['PROVIDER_VALIDATION_ERROR'];

@Component({
  selector: 'app-suppliers',
  templateUrl: './suppliers.component.html',
  styleUrl: './suppliers.component.scss',
  standalone: true,
  imports: [CommonModule, InputComponent, TranslateModule, ButtonComponent, AlertComponent],
})
export class SuppliersComponent implements OnInit, OnDestroy {
  userInfo: UserInfo;
  vatId: string = '';
  companyName: string | null = null;
  showValidationError: boolean = false;
  updateErrorKey: string | null = null;
  updateErrorType: 'error' | 'warning' = 'error';

  constructor(private apiService: ApiService, private translateService: TranslateService, private router: Router, private analytics: AnalyticsService) {
  
  }

  ngOnInit(): void {
    this.apiService.userInfo.subscribe((userInfo) => {
      this.userInfo = userInfo;
      this.companyName = userInfo?.accountName || null;
      this.vatId = userInfo?.fiscalId || '';
    })
  }

  ngOnDestroy(): void {
    // Lógica de limpieza si es necesario
  }

  validate() {
    console.log('Validating VAT ID:', this.vatId);
    
    // Resetear errores previos
    this.showValidationError = false;
    this.updateErrorKey = null;
    
    // Abrir ventana inmediatamente para evitar bloqueo del navegador
    const newWindow = window.open('', '_blank'); //NOSONAR
    
    this.apiService.validateFiscalId(this.vatId).pipe(
      switchMap((response) => {
        console.log('Validation response:', response);
        console.log('Is VAT ID valid?', response.valid);
        if (response) {
          console.log('VAT ID is valid. Updating fiscalId...');
          return this.apiService.updateFiscalId(this.vatId);
        } else {
          console.log('VAT ID is NOT valid. Skipping fiscalId update.');
          // Mostrar mensaje de error al usuario
          this.showValidationError = true;
          // Cerrar la ventana si la validación falla
          if (newWindow) {
            newWindow.close();
          }
          return of(null);
        }
      }),
      catchError((error) => {
        console.error('Error in validation process:', error);
        if (error?.status === 409 && error?.error?.error === 'PROVIDER_REGISTRATION_IN_PROGRESS' && error?.error?.url) {
          console.log('Registration already in progress. Redirecting to:', error.error.url);
          return of({ url: error.error.url as string });
        }
        if (newWindow) {
          newWindow.close();
        }
        // Mapear el código de error del backend al mensaje correspondiente
        const errorCode = error?.error?.error as string;
        const message = SUPPLIER_MESSAGES[errorCode] ?? DEFAULT_UPDATE_ERROR;
        this.updateErrorKey = message.id;
        this.updateErrorType = message.type;
        return of(null);
      })
    ).subscribe({
      next: (response) => {
        if (response) {
          console.log('FiscalId updated successfully:', response);
          if (response.url) {
            console.log('Opening URL from response:', response.url);
            // Redirigir la ventana ya abierta a la URL
            if (newWindow) {
              newWindow.location.href = response.url;
            }
          } else {
            // Si no hay URL, cerrar la ventana
            if (newWindow) {
              newWindow.close();
            }
          }
        } else {
          // Cerrar la ventana si no hay response válido
          if (newWindow) {
            newWindow.close();
          }
        }
      },
      error: (error) => {
        console.error('Error updating FiscalId:', error);
        // Cerrar la ventana si hay error
        if (newWindow) {
          newWindow.close();
        }
      }
    });
  }

  onVatIdChange(event: Event) {
    const newVatId = (event.target as HTMLInputElement).value;
    this.vatId = newVatId;
  }
}